import { memo } from 'react';
import styled, { css } from 'styled-components';
import { slugify } from '../../utils/slugify';
import { TypographyVariant } from '../../utils/theme';
import Tooltip from '../mantine-tooltip';
import Typography from '../typography/typography';

interface PopoverActionProps<T> {
  isDisabled?: boolean;
  data: T;
  ariaLabel?: string;
  label: string;
  handleClick: (data: T) => void;
  padding?: number;
  disabledTooltip?: string;
}

const PopoverAction = <T,>({
  ariaLabel,
  label,
  handleClick,
  isDisabled,
  data,
  padding,
  disabledTooltip,
}: PopoverActionProps<T>) => {
  return (
    <Tooltip label={isDisabled ? disabledTooltip : undefined} position="bottom">
      <StyledTypography
        variant={TypographyVariant.bodyMd1}
        onClick={() => {
          if (!isDisabled) handleClick(data);
        }}
        aria-label={ariaLabel}
        data-testid={slugify(label)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          const eventKey = event.key;
          if (!isDisabled && (eventKey === 'Enter' || eventKey === ' ')) {
            handleClick(data);
          }
        }}
        $padding={padding}
        $isDisabled={isDisabled}
      >
        {label}
      </StyledTypography>
    </Tooltip>
  );
};

const StyledTypography = styled(Typography)<{
  $padding?: number;
  $isDisabled?: boolean;
}>`
  ${({ theme, $padding, $isDisabled }) => css`
    padding: ${$padding ? theme.spacing($padding) : theme.spacing(1, 1.5)};
    cursor: ${$isDisabled ? 'not-allowed' : 'pointer'};
    color: ${$isDisabled ? theme.palette.neutral[70] : undefined};

    &:hover {
      background-color: ${theme.palette.neutral[50]};
    }
  `}
`;

export default memo(PopoverAction) as typeof PopoverAction;
